import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  ClipboardCheck, Star, AlertTriangle, Plus, ChevronRight,
  User, Calendar, Loader2, FileText, Shield, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMPETENCY_AREAS = [
  'Clinical Skills', 'Communication', 'Time Management', 'Documentation',
  'Patient Safety', 'Teamwork', 'Professional Conduct', 'Problem Solving'
];

const SCORE_LABELS = { 1: 'Needs Improvement', 2: 'Developing', 3: 'Competent', 4: 'Proficient', 5: 'Expert' };
const SCORE_COLORS = { 1: 'text-red-400', 2: 'text-orange-400', 3: 'text-amber-400', 4: 'text-emerald-400', 5: 'text-[#00A6A0]' };

export default function PerformanceReviews() {
  const { user } = useAuth();
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const [activeTab, setActiveTab] = useState('reviews');
  const [reviews, setReviews] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [reviewDialog, setReviewDialog] = useState(false);
  const [assessmentDialog, setAssessmentDialog] = useState(false);
  const [warningDialog, setWarningDialog] = useState(false);

  // Forms
  const [reviewForm, setReviewForm] = useState({
    caregiver_id: '', period: 'Q1 2026', overall_score: 3,
    categories: {}, strengths: '', improvements: '', goals: '', comments: ''
  });
  const [assessmentForm, setAssessmentForm] = useState({
    caregiver_id: '', competencies: COMPETENCY_AREAS.map(c => ({ name: c, level: 'developing', score: 3 })),
    overall_level: 'developing', notes: ''
  });
  const [warningForm, setWarningForm] = useState({
    caregiver_id: '', warning_type: 'verbal', severity: 'low',
    subject: '', description: '', action_required: '', follow_up_date: ''
  });

  useEffect(() => {
    fetchAll();
    if (isAdmin) fetchCaregivers();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [rRes, aRes, wRes] = await Promise.all([
      axios.get(`${API_URL}/api/performance-reviews`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/competency-assessments`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/staff-warnings`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    setReviews(rRes.data);
    setAssessments(aRes.data);
    setWarnings(wRes.data);
    setLoading(false);
  };

  const fetchCaregivers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/by-org?role=caregiver`, { withCredentials: true });
      setCaregivers(data);
    } catch (err) { console.error(err); }
  };

  const submitReview = async () => {
    if (!reviewForm.caregiver_id) return toast.error('Select a caregiver');
    try {
      await axios.post(`${API_URL}/api/performance-reviews`, reviewForm, { withCredentials: true });
      toast.success('Performance review created');
      setReviewDialog(false);
      setReviewForm({ caregiver_id: '', period: 'Q1 2026', overall_score: 3, categories: {}, strengths: '', improvements: '', goals: '', comments: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const publishReview = async (id) => {
    try {
      await axios.put(`${API_URL}/api/performance-reviews/${id}`, { status: 'published' }, { withCredentials: true });
      toast.success('Review published');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const submitAssessment = async () => {
    if (!assessmentForm.caregiver_id) return toast.error('Select a caregiver');
    const cg = caregivers.find(c => c.id === assessmentForm.caregiver_id);
    try {
      await axios.post(`${API_URL}/api/competency-assessments`, {
        ...assessmentForm, caregiver_name: cg?.name || ''
      }, { withCredentials: true });
      toast.success('Assessment created');
      setAssessmentDialog(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitWarning = async () => {
    if (!warningForm.caregiver_id || !warningForm.subject) return toast.error('Select caregiver and enter subject');
    try {
      await axios.post(`${API_URL}/api/staff-warnings`, warningForm, { withCredentials: true });
      toast.success('Warning issued');
      setWarningDialog(false);
      setWarningForm({ caregiver_id: '', warning_type: 'verbal', severity: 'low', subject: '', description: '', action_required: '', follow_up_date: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const acknowledgeWarning = async (id) => {
    try {
      await axios.put(`${API_URL}/api/staff-warnings/${id}/acknowledge`, {}, { withCredentials: true });
      toast.success('Warning acknowledged');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const tabs = [
    { id: 'reviews', label: 'Reviews', icon: ClipboardCheck, count: reviews.length },
    { id: 'assessments', label: 'Competencies', icon: Award, count: assessments.length },
    { id: 'warnings', label: 'Warnings', icon: AlertTriangle, count: warnings.length },
  ];

  const severityColors = { low: 'bg-amber-500/10 text-amber-400', medium: 'bg-orange-500/10 text-orange-400', high: 'bg-red-500/10 text-red-400' };
  const warningTypeLabels = { verbal: 'Verbal', written: 'Written', final: 'Final', suspension: 'Suspension' };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00A6A0]" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="performance-reviews-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">HR & Performance</h1>
            <p className="text-muted-foreground mt-1">Reviews, competencies, and staff management</p>
          </div>
        </div>

        <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={`whitespace-nowrap ${activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}`}
              onClick={() => setActiveTab(t.id)} data-testid={`hr-tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-1.5" />{t.label}
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-muted">{t.count}</span>
            </Button>
          ))}
        </div>

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {isAdmin && (
              <Button className="btn-primary" onClick={() => setReviewDialog(true)} data-testid="new-review-btn">
                <Plus className="w-4 h-4 mr-2" />New Review
              </Button>
            )}
            {reviews.length === 0 ? (
              <Card className="glass-card border-border"><CardContent className="p-8 text-center"><p className="text-muted-foreground">No performance reviews yet</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {reviews.map(r => (
                  <Card key={r.id} className="glass-card border-border" data-testid={`review-${r.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${SCORE_COLORS[r.overall_score] || 'text-muted-foreground'} bg-muted`}>{r.overall_score}/5</div>
                          <div>
                            <p className="text-sm text-foreground font-medium">{r.caregiver_name}</p>
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              <span>{r.period}</span>
                              <span className={`px-1.5 py-0.5 rounded-full ${r.status === 'published' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{r.status}</span>
                            </div>
                          </div>
                        </div>
                        {isAdmin && r.status === 'draft' && (
                          <Button size="sm" className="bg-[#00A6A0] hover:bg-[#008A85] text-[#0F1419] text-xs" onClick={() => publishReview(r.id)} data-testid={`publish-review-${r.id}`}>Publish</Button>
                        )}
                      </div>
                      {r.strengths && <p className="text-xs text-muted-foreground mt-2"><span className="text-emerald-400">Strengths:</span> {r.strengths}</p>}
                      {r.improvements && <p className="text-xs text-muted-foreground mt-1"><span className="text-amber-400">Improvements:</span> {r.improvements}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ASSESSMENTS TAB */}
        {activeTab === 'assessments' && (
          <div className="space-y-4">
            {isAdmin && (
              <Button className="btn-primary" onClick={() => setAssessmentDialog(true)} data-testid="new-assessment-btn">
                <Plus className="w-4 h-4 mr-2" />New Assessment
              </Button>
            )}
            {assessments.length === 0 ? (
              <Card className="glass-card border-border"><CardContent className="p-8 text-center"><p className="text-muted-foreground">No competency assessments yet</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {assessments.map(a => (
                  <Card key={a.id} className="glass-card border-border" data-testid={`assessment-${a.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm text-foreground font-medium">{a.caregiver_name}</p>
                          <p className="text-[10px] text-muted-foreground">Assessed by {a.assessor_name} - {new Date(a.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${a.overall_level === 'expert' ? 'bg-[#00A6A0]/10 text-[#00A6A0]' : a.overall_level === 'proficient' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{a.overall_level}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {a.competencies?.slice(0, 8).map((c, i) => (
                          <div key={i} className="p-2 bg-muted rounded-lg border border-border text-center">
                            <p className="text-[10px] text-muted-foreground truncate">{c.name}</p>
                            <p className={`text-sm font-bold ${SCORE_COLORS[c.score] || 'text-muted-foreground'}`}>{c.score}/5</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* WARNINGS TAB */}
        {activeTab === 'warnings' && (
          <div className="space-y-4">
            {isAdmin && (
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setWarningDialog(true)} data-testid="new-warning-btn">
                <Plus className="w-4 h-4 mr-2" />Issue Warning
              </Button>
            )}
            {warnings.length === 0 ? (
              <Card className="glass-card border-border"><CardContent className="p-8 text-center"><p className="text-muted-foreground">No staff warnings</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {warnings.map(w => (
                  <Card key={w.id} className="glass-card border-border" data-testid={`warning-${w.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className={`w-5 h-5 ${w.severity === 'high' ? 'text-red-400' : w.severity === 'medium' ? 'text-orange-400' : 'text-amber-400'}`} />
                          <div>
                            <p className="text-sm text-foreground font-medium">{w.caregiver_name} - {w.subject}</p>
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              <span className={`px-1.5 py-0.5 rounded-full ${severityColors[w.severity]}`}>{w.severity}</span>
                              <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{warningTypeLabels[w.warning_type]}</span>
                              {w.acknowledged && <span className="text-emerald-400">Acknowledged</span>}
                            </div>
                          </div>
                        </div>
                        {user?.role === 'caregiver' && !w.acknowledged && (
                          <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 text-xs" onClick={() => acknowledgeWarning(w.id)} data-testid={`ack-warning-${w.id}`}>Acknowledge</Button>
                        )}
                      </div>
                      {w.description && <p className="text-xs text-muted-foreground mt-2">{w.description}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Review Dialog */}
        <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="text-foreground">Create Performance Review</DialogTitle></DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Caregiver</label>
                <select value={reviewForm.caregiver_id} onChange={e => setReviewForm(f => ({ ...f, caregiver_id: e.target.value }))}
                  className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm h-10" data-testid="review-caregiver-select">
                  <option value="">Select caregiver</option>
                  {caregivers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-muted-foreground block mb-1">Period</label>
                  <Input value={reviewForm.period} onChange={e => setReviewForm(f => ({ ...f, period: e.target.value }))} className="bg-muted border-border text-foreground" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Overall Score (1-5)</label>
                  <Input type="number" min={1} max={5} value={reviewForm.overall_score} onChange={e => setReviewForm(f => ({ ...f, overall_score: parseInt(e.target.value) || 3 }))} className="bg-muted border-border text-foreground" /></div>
              </div>
              <div><label className="text-xs text-muted-foreground block mb-1">Strengths</label>
                <textarea value={reviewForm.strengths} onChange={e => setReviewForm(f => ({ ...f, strengths: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" /></div>
              <div><label className="text-xs text-muted-foreground block mb-1">Areas for Improvement</label>
                <textarea value={reviewForm.improvements} onChange={e => setReviewForm(f => ({ ...f, improvements: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" /></div>
              <div><label className="text-xs text-muted-foreground block mb-1">Goals</label>
                <textarea value={reviewForm.goals} onChange={e => setReviewForm(f => ({ ...f, goals: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" /></div>
              <Button className="w-full btn-primary" onClick={submitReview} data-testid="submit-review-btn">Create Review (Draft)</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assessment Dialog */}
        <Dialog open={assessmentDialog} onOpenChange={setAssessmentDialog}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="text-foreground">Competency Assessment</DialogTitle></DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Caregiver</label>
                <select value={assessmentForm.caregiver_id} onChange={e => setAssessmentForm(f => ({ ...f, caregiver_id: e.target.value }))}
                  className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm h-10" data-testid="assessment-caregiver-select">
                  <option value="">Select caregiver</option>
                  {caregivers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {assessmentForm.competencies.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                  <span className="text-sm text-foreground">{c.name}</span>
                  <select value={c.score} onChange={e => {
                    const updated = [...assessmentForm.competencies];
                    updated[i] = { ...c, score: parseInt(e.target.value) };
                    setAssessmentForm(f => ({ ...f, competencies: updated }));
                  }} className="bg-card border border-border rounded px-2 py-1 text-sm text-foreground">
                    {[1,2,3,4,5].map(s => <option key={s} value={s}>{s} - {SCORE_LABELS[s]}</option>)}
                  </select>
                </div>
              ))}
              <div><label className="text-xs text-muted-foreground block mb-1">Notes</label>
                <textarea value={assessmentForm.notes} onChange={e => setAssessmentForm(f => ({ ...f, notes: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" /></div>
              <Button className="w-full btn-primary" onClick={submitAssessment} data-testid="submit-assessment-btn">Save Assessment</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Warning Dialog */}
        <Dialog open={warningDialog} onOpenChange={setWarningDialog}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="text-red-400">Issue Staff Warning</DialogTitle></DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Caregiver</label>
                <select value={warningForm.caregiver_id} onChange={e => setWarningForm(f => ({ ...f, caregiver_id: e.target.value }))}
                  className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm h-10" data-testid="warning-caregiver-select">
                  <option value="">Select caregiver</option>
                  {caregivers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-muted-foreground block mb-1">Warning Type</label>
                  <select value={warningForm.warning_type} onChange={e => setWarningForm(f => ({ ...f, warning_type: e.target.value }))}
                    className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm h-10">
                    <option value="verbal">Verbal</option><option value="written">Written</option>
                    <option value="final">Final Warning</option><option value="suspension">Suspension</option>
                  </select>
                </div>
                <div><label className="text-xs text-muted-foreground block mb-1">Severity</label>
                  <select value={warningForm.severity} onChange={e => setWarningForm(f => ({ ...f, severity: e.target.value }))}
                    className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm h-10">
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </div>
              </div>
              <div><label className="text-xs text-muted-foreground block mb-1">Subject</label>
                <Input value={warningForm.subject} onChange={e => setWarningForm(f => ({ ...f, subject: e.target.value }))} className="bg-muted border-border text-foreground" data-testid="warning-subject-input" /></div>
              <div><label className="text-xs text-muted-foreground block mb-1">Description</label>
                <textarea value={warningForm.description} onChange={e => setWarningForm(f => ({ ...f, description: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" /></div>
              <div><label className="text-xs text-muted-foreground block mb-1">Action Required</label>
                <textarea value={warningForm.action_required} onChange={e => setWarningForm(f => ({ ...f, action_required: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" /></div>
              <Button className="w-full bg-red-600 hover:bg-red-700 text-white" onClick={submitWarning} data-testid="submit-warning-btn">Issue Warning</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
